import { motion, AnimatePresence } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { staggerContainer, fadeInUp } from '@/lib/animations';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { ClockIcon, BoltIcon } from '@heroicons/react/24/outline';

export function Solution() {
  const { t } = useTranslation();
  const { ref, isInView } = useScrollAnimation();
  const [minutes, setMinutes] = useState<number | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showResult, setShowResult] = useState(false);

  // Calculate yearly time lost
  const yearlyHoursLost = minutes ? Math.round((minutes * 5 * 52) / 60) : 0; // 5 searches per week, 52 weeks

  useEffect(() => {
    if (hasInteracted && minutes !== null) {
      const timer = setTimeout(() => setShowResult(true), 600);
      return () => clearTimeout(timer);
    }
  }, [hasInteracted, minutes]);

  const handleSliderChange = (value: number) => {
    setMinutes(value);
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  const presetOptions = [5, 10, 15, 30];

  return (
    <section className="py-20 md:py-32 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white dark:from-slate-900 dark:to-slate-800" />

      {/* Decorative elements */}
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-primary-200/30 dark:bg-primary-900/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-72 h-72 bg-purple-200/30 dark:bg-purple-900/20 rounded-full blur-3xl" />

      <Container className="relative">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="max-w-4xl mx-auto"
        >
          {/* The Uncomfortable Question */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
                {t('solution.question.line1')}
                <br />
                <span className="text-gradient">{t('solution.question.line2')}</span>
                <br />
                {t('solution.question.line3')}
              </h2>
            </motion.div>
          </motion.div>

          {/* Interactive Input */}
          <motion.div
            variants={fadeInUp}
            className="mb-16"
          >
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-slate-700 p-8 md:p-12">
              {/* Preset buttons */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {presetOptions.map((option) => (
                  <motion.button
                    key={option}
                    onClick={() => handleSliderChange(option)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-6 py-3 rounded-xl font-semibold text-lg transition-all ${
                      minutes === option
                        ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {option} min
                  </motion.button>
                ))}
                <motion.button
                  onClick={() => handleSliderChange(60)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-6 py-3 rounded-xl font-semibold text-lg transition-all ${
                    minutes === 60
                      ? 'bg-red-600 text-white shadow-lg shadow-red-500/30'
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                  }`}
                >
                  +60 min 😰
                </motion.button>
              </div>

              {/* Current selection display */}
              <AnimatePresence mode="wait">
                {minutes !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="text-center mb-8"
                  >
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-800">
                      <ClockIcon className="w-6 h-6 text-red-500" />
                      <span className="text-xl font-medium text-red-700 dark:text-red-400">
                        {t('solution.yourAnswer')}: <span className="font-bold">{minutes} {t('solution.minutes')}</span>
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* The Reveal */}
              <AnimatePresence>
                {showResult && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="relative"
                  >
                    {/* Comparison */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      {/* Their reality */}
                      <motion.div
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6 border border-red-200 dark:border-red-800"
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                            <ClockIcon className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-semibold text-red-700 dark:text-red-400">{t('solution.today')}</span>
                        </div>
                        <p className="text-4xl font-bold text-red-600 dark:text-red-400 mb-2">
                          {minutes} {t('solution.minutes')}
                        </p>
                        <p className="text-red-600/70 dark:text-red-400/70 text-sm">
                          {t('solution.perSearch')}
                        </p>
                        <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-600/70 dark:text-red-400/70">
                            {t('solution.yearlyLoss')}:
                          </p>
                          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {yearlyHoursLost} {t('solution.hoursPerYear')}
                          </p>
                        </div>
                      </motion.div>

                      {/* Affogato */}
                      <motion.div
                        initial={{ x: 30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white relative overflow-hidden"
                      >
                        {/* Glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />

                        <div className="relative">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                              <BoltIcon className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-semibold">Con Affogato</span>
                          </div>
                          <p className="text-5xl font-bold mb-2">
                            3 {t('solution.seconds')}
                          </p>
                          <p className="text-primary-100 text-sm">
                            {t('solution.oneClick')}
                          </p>
                          <div className="mt-4 pt-4 border-t border-white/20">
                            <p className="text-sm text-primary-100">
                              {t('solution.youSave')}:
                            </p>
                            <p className="text-2xl font-bold">
                              {yearlyHoursLost} {t('solution.hoursPerYear')} ✨
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Impact message */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="text-center"
                    >
                      <p className="text-xl md:text-2xl text-gray-600 dark:text-slate-400">
                        {t('solution.impactMessage.part1')}{' '}
                        <span className="font-bold text-primary-600 dark:text-primary-400">
                          {yearlyHoursLost} {t('solution.hours')}
                        </span>{' '}
                        {t('solution.impactMessage.part2')}
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Prompt to interact */}
              {!hasInteracted && (
                <motion.p
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-center text-gray-500 dark:text-slate-500"
                >
                  👆 {t('solution.selectTime')}
                </motion.p>
              )}
            </div>
          </motion.div>

          {/* Bottom CTA teaser */}
          <motion.div variants={fadeInUp} className="text-center">
            <p className="text-gray-500 dark:text-slate-500 text-sm">
              {t('solution.bottomTeaser')}
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
}
